import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useProjectImport } from '../../api/hooks/useProjectImport.js';
import { useNotifications } from '../../context/NotificationContext.js';

const MAX_BYTES = 100 * 1024 * 1024; // 100 MB

interface WizardState {
  step: number;
  file: File | null;
  base64: string | null;
  error: string | null;
}

function formatMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function ProjectImportWizardPage() {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const importMutation = useProjectImport();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<WizardState>({
    step: 0,
    file: null,
    base64: null,
    error: null,
  });

  const steps = [
    t('import.step1Label', 'Datei wählen'),
    t('import.step2Label', 'Vorschau'),
    t('import.step3Label', 'Bestätigung'),
    t('import.step4Label', 'Ergebnis'),
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setState((s) => ({ ...s, error: t('import.fileTooLarge', 'Datei zu groß (max. 100 MB).') }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        setState((s) => ({ ...s, error: t('import.readError', 'Fehler beim Lesen der Datei.') }));
        return;
      }
      const base64 = result.split(',', 2)[1] ?? '';
      setState({ step: 1, file, base64, error: null });
    };
    reader.onerror = () =>
      setState((s) => ({ ...s, error: t('import.readError', 'Fehler beim Lesen der Datei.') }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!state.file || !state.base64) return;
    try {
      await importMutation.mutateAsync({ content: state.base64, filename: state.file.name });
      setState((s) => ({ ...s, step: 3, error: null }));
      notify(t('import.successNotification', 'Projekt-Import erfolgreich.'), 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, step: 3, error: message }));
      notify(t('import.errorNotification', 'Projekt-Import fehlgeschlagen: {{error}}', { error: message }), 'error');
    }
  };

  const handleReset = () => {
    setState({ step: 0, file: null, base64: null, error: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBack = () => {
    if (state.step === 0) {
      navigate('/projects');
      return;
    }
    setState((s) => ({ ...s, step: s.step - 1, error: null }));
  };

  const handleNext = () => {
    setState((s) => ({ ...s, step: s.step + 1, error: null }));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/projects')} aria-label={t('actions.back', 'Zurück')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t('import.title', 'Projekt importieren')}
        </Typography>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={state.step} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {state.step === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('import.step1Description', 'Wähle eine zuvor exportierte ZIP-Datei aus.')}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              data-testid="project-import-file-input"
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('import.selectFile', 'Datei auswählen')}
            </Button>
            {state.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {state.error}
              </Alert>
            )}
          </Box>
        )}

        {state.step === 1 && state.file && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('import.step2Description', 'Prüfe die Datei vor dem Import.')}
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('import.previewFilename', 'Datei')}:</strong> {state.file.name}
              </Typography>
              <Typography variant="body2">
                <strong>{t('import.previewSize', 'Größe')}:</strong> {formatMB(state.file.size)}
              </Typography>
            </Stack>
          </Box>
        )}

        {state.step === 2 && state.file && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('import.confirmHint', 'Der Import erstellt ein neues Projekt. Bestehende Projekte werden nicht überschrieben.')}
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('import.confirmFile', 'Datei zum Import: {{filename}}', { filename: state.file.name })}
            </Typography>
            {importMutation.isPending && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">{t('import.uploading', 'Import läuft...')}</Typography>
              </Stack>
            )}
          </Box>
        )}

        {state.step === 3 && (
          <Box>
            {state.error ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <ErrorOutlineIcon color="error" />
                <Typography variant="h6">
                  {t('import.failureTitle', 'Import fehlgeschlagen')}
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">
                  {t('import.successTitle', 'Import erfolgreich')}
                </Typography>
              </Stack>
            )}
            {state.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {state.error}
              </Alert>
            )}
            {!state.error && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('import.successDetail', 'Das Projekt wurde erfolgreich importiert.')}
              </Alert>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 4 }}>
          <Button onClick={handleBack} disabled={importMutation.isPending}>
            {state.step === 0 ? t('actions.cancel', 'Abbrechen') : t('actions.back', 'Zurück')}
          </Button>

          {state.step === 0 && (
            <Button
              variant="contained"
              disabled={!state.file || !state.base64}
              onClick={handleNext}
            >
              {t('actions.next', 'Weiter')}
            </Button>
          )}

          {state.step === 1 && (
            <Button variant="contained" onClick={handleNext}>
              {t('actions.next', 'Weiter')}
            </Button>
          )}

          {state.step === 2 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={importMutation.isPending}
              startIcon={importMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {t('import.submit', 'Importieren')}
            </Button>
          )}

          {state.step === 3 && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleReset}>
                {t('import.importAgain', 'Erneut importieren')}
              </Button>
              <Button variant="contained" onClick={() => navigate('/projects')}>
                {t('actions.done', 'Fertig')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
